import type { Editor as CoreEditor, FocusPosition } from '@tiptap/core'
import type { Transaction } from '@tiptap/pm/state'
import type { JSONContent } from '@tiptap/react'
import { EditorContext, useEditor } from '@tiptap/react'
import { clone, pick } from 'rambda'
import type { HtmlHTMLAttributes } from 'react'
import React, { forwardRef, useMemo, useRef } from 'react'
import { useLatest, useUpdateEffect } from 'react-use'

import { filterUploadPlaceholdersFromJSON, getExtensions, refreshEditorPlaceholder } from './helper'
import * as SC from './styles'
import { normalizeRichTextPastedHTML, TableControls } from './Table'
import type { ToolbarProps } from './Toolbar'
import { Toolbar } from './Toolbar'
import type { ToolbarStyles } from './Toolbar/Toolbar'

type TipTapStyles = 'editorContainer' | 'editorContent' | ToolbarStyles

export interface TiptapEditorProps extends Pick<ToolbarProps, 'config' | 'isMobile'>, HtmlHTMLAttributes<HTMLDivElement> {
    value?: string | null | JSONContent
    onChange?: (json: JSONContent, html?: string) => void
    onEditorBlur?: (props: { editor: CoreEditor; event: FocusEvent; transaction: Transaction }) => void
    autofocus?: FocusPosition
    readonly?: boolean
    minHeight?: number | string
    containerProps?: HtmlHTMLAttributes<HTMLDivElement>
    styles?: Partial<Record<TipTapStyles, React.CSSProperties>>
    disableToolbar?: boolean
    disabled?: boolean
}

export const TiptapEditor = forwardRef<HTMLDivElement, TiptapEditorProps>(
    (
        {
            value,
            onChange,
            onEditorBlur,
            autofocus = false,
            readonly,
            config,
            containerProps,
            styles,
            disableToolbar,
            disabled,
            minHeight,
            isMobile,
            placeholder,
            ...props
        },
        ref
    ) => {
        const latest = useLatest({ onChange, placeholder })

        const isCompositionInputRef = useRef(false)

        const editor = useEditor(
            {
                extensions: getExtensions({
                    ...config,
                    placeholder: () => latest.current.placeholder ?? '',
                    readonly
                }),
                autofocus,
                content: value,
                editable: !(disabled || readonly),
                editorProps: {
                    handleDOMEvents: {
                        compositionstart: () => {
                            isCompositionInputRef.current = true
                        },
                        compositionend: view => {
                            isCompositionInputRef.current = false
                            const json = filterUploadPlaceholdersFromJSON(view.state.doc.toJSON())
                            json && latest.current.onChange?.(json)
                        }
                    },

                    transformPastedHTML(html) {
                        return normalizeRichTextPastedHTML(html)
                    }
                },
                onBlur: (...args) => onEditorBlur?.(...args),
                onUpdate: ({ editor: e }) => {
                    if (isCompositionInputRef.current) {
                        return
                    }
                    // 过滤旧数据中的上传占位节点，再输出；需要 clone 避免 attrs 原型链问题
                    const json = filterUploadPlaceholdersFromJSON(clone(e.getJSON()))
                    json && latest.current.onChange?.(json, e.getHTML())
                }
            },
            [readonly]
        )

        useUpdateEffect(() => {
            refreshEditorPlaceholder(editor)
        }, [editor, placeholder])

        // 恢复选中range
        useUpdateEffect(() => {
            if (editor && !editor.isDestroyed && !editor.isFocused) {
                editor
                    .chain()
                    .setContent(value || null, { emitUpdate: false })
                    .setTextSelection({ from: editor.state.selection.from, to: editor.state.selection.to })
                    .run()
            }
        }, [value])

        const contentStyle = useMemo(
            () => ({
                padding: readonly ? undefined : 8,
                ...styles?.editorContent
            }),
            [readonly, styles?.editorContent]
        )

        const toolbarStyle = useMemo(() => {
            return styles ? pick(['toolbarContainer', 'toolbarMenuList'], styles) : undefined
        }, [styles])

        const contextValue = useMemo(() => ({ editor }), [editor])

        return (
            <EditorContext.Provider value={contextValue}>
                <SC.Container ref={ref} {...props} style={styles?.editorContainer}>
                    {!!editor && !readonly && !disableToolbar && (
                        <Toolbar disabled={disabled} isMobile={isMobile} styles={toolbarStyle} config={config} />
                    )}
                    <SC.Content
                        editor={editor}
                        style={contentStyle}
                        isEditable={editor?.isEditable}
                        minHeight={minHeight}
                        {...containerProps}
                    />
                    {!!editor && config?.mode === 'all' && !readonly && <TableControls editor={editor} isMobile={isMobile} />}
                </SC.Container>
            </EditorContext.Provider>
        )
    }
)
